import { useEffect, useState, useCallback } from "react";
import { Loader2, Undo2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, useAdmin } from "@/components/admin/ui";

const money = (c) => (c == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(c / 100));
const TABS = [["", "All"], ["requested", "Requested"], ["approved", "Approved (refunded)"], ["rejected", "Rejected"]];
const CLS = { requested: "bg-[#8A5A44]/15 text-[#8A5A44]", approved: "bg-[#2E7D46]/15 text-[#2E7D46]", rejected: "bg-[#B04A3A]/15 text-[#B04A3A]" };

export default function AdminCommerceReturns() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => { api.admReturns({ status: tab || undefined }).then(setRows).catch(() => setRows([])); }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (r, action) => {
    if (action === "approve" && !window.confirm(`Approve this return and issue a Stripe refund for ${money(r.order?.total_cents)}? This cannot be undone.`)) return;
    setBusy(r.id);
    try {
      const res = await api.admReturnAction(r.id, action);
      toast.success(res.status === "approved" ? "Refund issued via Stripe" : "Return rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-commerce-returns">
      <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Returns & refunds</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2">Approving a return issues a real Stripe refund on the original payment.</p>

      <div className="flex gap-1.5 mt-5 mb-4 flex-wrap">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs border ${tab === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`returns-tab-${k || "all"}`}>{l}</button>)}
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-[#5C6B61]"><Undo2 size={34} className="mx-auto text-[#C7C2B8]" /><p className="mt-3">No return requests.</p></div>
      ) : (
        <div className="space-y-3" data-testid="returns-list">
          {rows.map((r) => (
            <div key={r.id} className="border border-[#E5E2DC] bg-white p-4" data-testid={`return-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#1F2A22]">…{r.session_id.slice(-10)}</span>
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 ${CLS[r.status] || ""}`}>{r.status}</span>
                    {r.order && <span className="text-sm text-[#1F2A22] font-medium">{money(r.order.total_cents)}</span>}
                  </div>
                  <p className="text-[#5C6B61] text-sm mt-1.5">{r.reason || <span className="italic text-[#8A8478]">No reason provided</span>}</p>
                  <p className="text-[11px] text-[#8A8478] mt-1">{r.email} · {(r.order?.items_summary || []).map((it) => `${it.title} ×${it.quantity}`).join(", ")}</p>
                </div>
                {canEdit && r.status === "requested" && (
                  <div className="flex flex-col gap-2 shrink-0">
                    <button disabled={busy === r.id} onClick={() => act(r, "approve")} className="text-xs border border-[#2E7D46] text-[#2E7D46] px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`return-approve-${r.id}`}>{busy === r.id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve & refund</button>
                    <button disabled={busy === r.id} onClick={() => act(r, "reject")} className="text-xs border border-[#B04A3A] text-[#B04A3A] px-2 py-1 inline-flex items-center gap-1 disabled:opacity-40" data-testid={`return-reject-${r.id}`}><X size={12} /> Reject</button>
                  </div>
                )}
                {r.refund_id && <span className="text-[11px] text-[#2E7D46] shrink-0">Refunded</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
